import { CommonModule } from '@angular/common';
import { Component, ContentChild, EventEmitter, inject, Input, OnChanges, OnInit, Output, SimpleChanges, TemplateRef } from '@angular/core';
import { ControlValueAccessor, FormsModule } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { ListboxModule } from 'primeng/listbox';
import { ListboxScrollToSelectedItemDirective } from '@lx/quote-tool/lite/shared/ui/directives';
import { getCustomFormComponentControlValueAccessorProvider } from '@lx/quote-tool/lite/shared/utils';
import { KeyboardFocusDirective } from '@lx/shared/ui/keyboard-focus';
import { SidePanelComponent } from '../../layout';
import { ListBoxComponent } from '../list-box/list-box.component';
import { SearcherComponent } from '../searcher/searcher.component';
import { SidePanelService } from '@lx/quote-tool/lite/shared/services';

@Component({
  standalone: true,
  selector: 'lx-dropdown-side-panel-field',
  templateUrl: './dropdown-side-panel-field.component.html',
  styleUrls: ['./dropdown-side-panel-field.component.scss'],
  imports: [
    SidePanelComponent,
    FormsModule,
    CommonModule,
    InputTextModule,
    SearcherComponent,
    ListBoxComponent,
    ListboxModule,
    ListboxScrollToSelectedItemDirective,
    KeyboardFocusDirective,
  ],
  providers: [getCustomFormComponentControlValueAccessorProvider(DropdownSidePanelFieldComponent)],
})
export class DropdownSidePanelFieldComponent<T> implements OnChanges, OnInit, ControlValueAccessor {
  @ContentChild('itemTemplate', { static: false }) itemTemplate: TemplateRef<unknown> | undefined;

  @Input() options: T[] = [];
  @Input() optionLabel = '';
  @Input() optionValue = '';
  @Input() title = '';
  @Input() filter = false;
  @Input() filterBy = '';
  @Input() placeholder = '';
  @Input() autocomplete = '';

  @Output() blurred = new EventEmitter<void>();

  selectedOption = {};
  filteredOptions = [];
  selectedValue: string = null;
  touched = false;
  dialogVisible = false;
  activePlaceHolder = false;

  private readonly sidePanelService = inject(SidePanelService);

  ngOnChanges({ options }: SimpleChanges): void {
    if (options.currentValue && options.currentValue.length) {
      this.selectedOption = this.getSelectedOptionFromOptions(this.selectedValue);
    }
  }

  ngOnInit(): void {
    this.selectedOption = this.getDefaultSelectedOption();
  }

  onModelChange: (fieldValue: string) => void = () => {
    // onModelChange triggered but not registered.
  };

  onTouched: () => void = () => {
    //  onTouched triggered but not registered.
  };

  registerOnChange(onModelChange: (selectedValue: string) => void): void {
    this.onModelChange = onModelChange;
  }

  registerOnTouched(onTouched: () => void): void {
    this.onTouched = onTouched;
  }

  writeValue(selectedValue: string): void {
    this.selectedValue = selectedValue;
    this.selectedOption = this.getSelectedOptionFromOptions(selectedValue);
  }

  updateSelectedOption(selectedValue: string) {
    this.onModelChange(selectedValue);
    this.writeValue(selectedValue);
    this.hideDialog();
  }

  filterOption(filter: string) {
    this.filteredOptions = this.options.filter((option) => option[this.filterBy].toLowerCase().includes(filter));
  }

  openSelectorComponent() {
    this.dialogVisible = true;
    this.filteredOptions = this.options;
  }

  hideDialog() {
    this.sidePanelService.hidePanel(() => (this.dialogVisible = false));
    this.markAsTouched();
  }

  getDefaultSelectedOption() {
    this.activePlaceHolder = true;
    return { [this.optionLabel]: this.placeholder, [this.optionValue]: this.selectedValue };
  }

  getSelectedOptionFromOptions(selectedValue: string) {
    const selectedValueFromOptions = this.options?.find((option) => option[this.optionValue] === selectedValue);
    if (selectedValueFromOptions) {
      this.activePlaceHolder = false;
      return selectedValueFromOptions;
    }

    return this.getDefaultSelectedOption();
  }

  private markAsTouched(): void {
    if (!this.touched) {
      this.blurred.emit();
      this.onTouched();
      this.touched = true;
    }
  }
}
